// Centralised live-reviews fetcher.
// All UI surfaces that need rating + count should pull from THIS module so
// the static "4.9★ from 4,300+ reviews" copy never sneaks in again.
import { useEffect, useState } from "react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TTL_MS = 6 * 60 * 60 * 1000;
const _memo = {};

async function fetchReviewsForStore(slug) {
    const now = Date.now();
    if (_memo[slug] && _memo[slug].expires > now) return _memo[slug].data;
    const res = await fetch(`${API}/reviews/${slug}`);
    if (!res.ok) throw new Error("reviews fetch failed");
    const data = await res.json();
    _memo[slug] = { expires: now + TTL_MS, data };
    return data;
}

/**
 * useLiveReviews(slug)
 *   → { rating, count, mapsUrl, reviews, loaded }
 * Returns sensible fallback values while loading so UI never flashes
 * "undefined". Fallback uses the LOCATIONS static values for the slug; if
 * unknown, defaults to 4.9 / 0.
 */
export function useLiveReviews(slug, fallbackRating = 4.9, fallbackCount = 0) {
    const [data, setData] = useState(null);
    useEffect(() => {
        let cancelled = false;
        if (!slug) return;
        fetchReviewsForStore(slug)
            .then((d) => {
                if (!cancelled) Promise.resolve().then(() => setData(d));
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, [slug]);
    return {
        rating: data?.rating ?? fallbackRating,
        count: data?.user_rating_count ?? fallbackCount,
        mapsUrl: data?.google_maps_uri || null,
        reviews: data?.reviews || [],
        loaded: !!data,
    };
}

/**
 * useLiveAggregateReviews([slug1, slug2, ...])
 *   → { rating: weighted_average, count: sum, loaded }
 * Used wherever marketing copy aggregates across both stores
 * (Home stats grid, brand-story paragraph, footer copy, etc.).
 */
export function useLiveAggregateReviews(slugs = []) {
    const [data, setData] = useState(null);
    useEffect(() => {
        let cancelled = false;
        if (!slugs.length) return;
        Promise.all(slugs.map((s) => fetchReviewsForStore(s).catch(() => null)))
            .then((arr) => {
                if (cancelled) return;
                let totalCount = 0;
                let weightedRating = 0;
                let n = 0;
                for (const r of arr) {
                    if (!r) continue;
                    const c = Number(r.user_rating_count || 0);
                    const v = Number(r.rating || 0);
                    if (c > 0 && v > 0) {
                        totalCount += c;
                        weightedRating += v * c;
                        n += 1;
                    }
                }
                Promise.resolve().then(() =>
                    setData({
                        rating: totalCount > 0 ? weightedRating / totalCount : 0,
                        count: totalCount,
                        loaded: n > 0,
                    })
                );
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, [slugs.join("|")]);
    return data || { rating: 4.9, count: 0, loaded: false };
}
